import { AlertOutlined, FileAddOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Layout,
  Modal,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useForm } from 'antd/es/form/Form';
import { Moment } from 'moment-timezone';
import moment from 'moment/moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as CompaniesSlice from 'slices/companies';
import {
  NotificationPreference,
  NotificationSettings,
  SchedulingPreference,
  useCompanies,
  Violation,
} from 'slices/companies';
import * as DriversSlices from 'slices/drivers';
import { useDrivers } from 'slices/drivers';
import * as ManualPatchSlice from 'slices/manualPatch';
import { useMyAccount } from 'slices/myAccount';
import { v4 } from 'uuid';
import FilterDropdown from '../../components/FilterDropdown';
import { useAuthConnectionEffect } from '../../hooks/useAuthConnectionEffect';
import { useAppDispatch } from '../../store/store';
import { getDriverTimezone } from '../../utils/driver';
import { fuzzySelectFilter, getContainer } from '../../utils/html';
import { timezones } from '../../utils/timezone';

interface NotificationOptions extends Omit<NotificationSettings, 'time'> {
  notificationTime?: Moment | null;
}

const NotificationPreferenceLabels: Record<NotificationPreference, string> = {
  'hos&teleport': 'Only Teleport and HOS violations',
  all: 'All violations',
};

const SchedulingPreferenceLabels: Record<SchedulingPreference, string> = {
  realtime: 'Realtime',
  scheduled: 'Scheduled (with defined frequency)',
};

export const ViolationsPage: FC = () => {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();

  const [
    companyId,
    setCompanyId,
  ] = useState<string | null>(null);
  const [
    driverId,
    setDriverId,
  ] = useState<string | null>(null);
  const [
    violations,
    setViolations,
  ] = useState<Violation[]>();
  const [
    filteredViolations,
    setFilteredViolations,
  ] = useState(violations);
  const [
    showModal,
    setShowModal,
  ] = useState<boolean>(false);

  const { myAccount, myAccountLoading, myAccountSubscribed } = useMyAccount();
  const { companiesById, companiesSubscribed, companiesLoading } = useCompanies();
  const { driversById, driversLoading, driversSubscribed } = useDrivers(companyId);

  const [form] = useForm<NotificationOptions>();
  const [
    formLoading,
    setFormLoading,
  ] = useState<boolean>(false);

  // Track scheduling preference to conditionally disable time and frequency fields
  const [
    schedulingPreference,
    setSchedulingPreference,
  ] = useState<SchedulingPreference>('scheduled');
  const [
    targetedNotificationEnabled,
    setTargetedNotificationsEnabled,
  ] = useState(false);
  const [
    summaryNotificationEnabled,
    setSummaryNotificationsEnabled,
  ] = useState(false);

  // TODO: replace with Form.useWatch as soon as antd is updated to v4.20.0+
  // Handle scheduling preference change
  const handleSchedulingChange = (e: RadioChangeEvent) => {
    setSchedulingPreference(e.target.value);
  };
  const handleToggleTargetedNotifications = (e: CheckboxChangeEvent) => {
    setTargetedNotificationsEnabled(e.target.checked);
  };
  const handleToggleSummaryNotifications = (e: CheckboxChangeEvent) => {
    setSummaryNotificationsEnabled(e.target.checked);
  };

  useAuthConnectionEffect(() => {
    if (companyId) {
      dispatch(DriversSlices.subscribe(companyId));
      return () => {
        dispatch(DriversSlices.unsubscribe(companyId));
      };
    }
  }, [
    companyId,
  ]);

  useAuthConnectionEffect(() => {
    dispatch(CompaniesSlice.subscribe());
    return () => {
      dispatch(CompaniesSlice.unsubscribe());
    };
  }, []);

  const fetchViolations = useCallback(async () => {
    return await appDispatch(CompaniesSlice.getViolations());
  }, [appDispatch]);

  const fetchViolationNotificationSettings = useCallback(async () => {
    return await appDispatch(CompaniesSlice.getViolationNotificationSettings());
  }, [appDispatch]);

  useAuthConnectionEffect(() => {
    fetchViolations().then((data) => {
      setViolations(data);
    });
  }, [fetchViolations]);

  useEffect(() => {
    if (violations === undefined) {
      return;
    }

    if (!companyId) {
      setFilteredViolations(violations);
      return;
    }

    if (!driverId) {
      setFilteredViolations(violations.filter((violation) => violation.companyId === companyId));
    } else {
      setFilteredViolations(
        violations.filter((violation) => violation.companyId === companyId && violation.driverId === driverId)
      );
    }
  }, [
    companyId,
    driverId,
    violations,
  ]);

  useEffect(() => {
    if (showModal) {
      setFormLoading(true);
      fetchViolationNotificationSettings().then((data) => {
        setFormLoading(false);
        setSchedulingPreference(data?.scheduling ?? 'scheduled');
        setTargetedNotificationsEnabled(data?.targetedNotificationEnabled ?? !!data);
        setSummaryNotificationsEnabled(data?.summaryNotificationEnabled ?? false);
        form.setFieldsValue({
          targetedNotificationEnabled: data?.targetedNotificationEnabled ?? !!data, // Here we use fallback until the value is populated
          summaryNotificationEnabled: data?.summaryNotificationEnabled ?? false,
          summaryGroupId: data?.summaryGroupId || null,
          notificationTime: data?.time ? moment(data.time) : null,
          message: data?.message,
          shortcut: data?.shortcut,
          frequency: data?.frequency ?? 24,
          preference: data?.preference ?? 'hos&teleport',
          scheduling: data?.scheduling ?? 'scheduled',
        });
      });
    }
  }, [showModal]);

  const regulationsFilters = useMemo(() => {
    const set = new Set<string>();
    (filteredViolations || []).forEach((violation) => {
      (violation.regulations || []).forEach((regulation) => {
        if (regulation) {
          set.add(regulation);
        }
      });
    });
    return [...set].sort().map((regulation) => ({ text: regulation, value: regulation }));
  }, [filteredViolations]);

  const violationsFilters = useMemo(() => {
    const set = new Set<string>();
    (filteredViolations || []).forEach((violation) => {
      (violation.violations || []).forEach((regulation) => {
        if (regulation) {
          set.add(regulation);
        }
      });
    });
    return [...set].sort().map((r) => ({ text: r, value: r }));
  }, [filteredViolations]);

  // track selected filters per column so we can show counts in headers
  const [
    filterSelections,
    setFilterSelections,
  ] = useState<Record<string, string[]>>({});

  const handleTableChange = useCallback((_, filters) => {
    const normalized: Record<string, string[]> = {};
    Object.entries(filters || {}).forEach(
      ([
        key,
        value,
      ]) => {
        if (!value) {
          normalized[key] = [];
        } else if (Array.isArray(value)) {
          normalized[key] = value as string[];
        } else {
          normalized[key] = [String(value)];
        }
      }
    );
    setFilterSelections(normalized);
  }, []);

  const [
    patchIsBeingCreated,
    setPatchIsBeingCreated,
  ] = useState<boolean>(false);

  const createManualPatch = async (companyId: string, driverId: string) => {
    setPatchIsBeingCreated(true);
    const clientResourceId = v4();
    const driver = await appDispatch(DriversSlices.getDriver(driverId, companyId));
    const company = companiesById[companyId];
    if (!driver || !company) {
      setPatchIsBeingCreated(false);
      return;
    }

    const timezone = getDriverTimezone(company, driver);
    const from = moment.tz(timezones[timezone]).subtract(9, 'days').toISOString();
    const to = moment.tz(timezones[timezone]).toISOString();
    const { data } = await appDispatch(
      ManualPatchSlice.create({
        clientResourceId,
        companyId,
        driverId,
        type: 'days',
        from,
        to,
      })
    );
    if (data?.id) {
      const { status } = await appDispatch(ManualPatchSlice.fetch(data.id, 0, [driverId]));
      if (status === 'ok') {
        setTimeout(() => {
          window.open(`/activity/${data.id}`, '_blank')?.focus();
          setPatchIsBeingCreated(false);
        }, 1000);
      } else {
        setPatchIsBeingCreated(false);
      }
    } else {
      setPatchIsBeingCreated(false);
    }
  };

  return (
    <Spin spinning={myAccountLoading || !myAccountSubscribed}>
      <Layout className={'accounts-list-container'}>
        <Layout.Content>
          <Row justify={'space-between'}>
            <Col span={6}>
              <Typography.Title style={{ margin: '16px 0' }} level={3}>
                VIOLATIONS
              </Typography.Title>
            </Col>
            {[
              'admin',
              'manager',
              'tech_support',
            ].includes(myAccount?.role || '') && (
              <Col
                span={3}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Button icon={<AlertOutlined />} key="createButton" type="primary" onClick={() => setShowModal(true)}>
                  NOTIFICATIONS
                </Button>
                <Modal
                  visible={showModal}
                  title="AUTOMATIC NOTIFICATIONS"
                  okText="CONFIRM"
                  cancelText="CANCEL"
                  onCancel={() => {
                    form.resetFields();
                    setShowModal(false);
                  }}
                  confirmLoading={formLoading}
                  onOk={() => {
                    form.submit();
                  }}
                  width="fit-content"
                  forceRender
                >
                  <Spin spinning={formLoading}>
                    <Form<NotificationOptions>
                      form={form}
                      layout="vertical"
                      name="form_in_modal"
                      onFinish={async (values) => {
                        setFormLoading(true);
                        await appDispatch(
                          CompaniesSlice.setViolationNotifications({
                            targetedNotificationEnabled: values.targetedNotificationEnabled,
                            time: values.notificationTime ? values.notificationTime.toDate() : null,
                            message: values.message,
                            shortcut: values.shortcut || null,
                            frequency: values.frequency ? Number(values.frequency) : null,
                            preference: values.preference ?? null,
                            summaryNotificationEnabled: values.summaryNotificationEnabled,
                            summaryGroupId: values.summaryGroupId ? Number(values.summaryGroupId) : null,
                            scheduling: values.scheduling ?? null,
                          })
                        );
                        setFormLoading(false);
                        setShowModal(false);
                      }}
                    >
                      <h4>Schedule</h4>
                      <Form.Item<NotificationOptions>
                        name="scheduling"
                        label="Select between realtime and scheduled notifications"
                      >
                        <Radio.Group
                          buttonStyle="solid"
                          onChange={handleSchedulingChange}
                          disabled={!targetedNotificationEnabled && !summaryNotificationEnabled}
                        >
                          {Object.entries(SchedulingPreferenceLabels).map(
                            ([
                              option,
                              label,
                            ]) => (
                              <Radio key={option} value={option}>
                                {label}
                              </Radio>
                            )
                          )}
                        </Radio.Group>
                      </Form.Item>
                      <div style={{ display: 'flex', columnGap: '1rem' }}>
                        <Form.Item<NotificationOptions>
                          name="notificationTime"
                          label="Start time"
                          help={`Local Time (${moment.tz.guess()})`}
                        >
                          <DatePicker
                            allowClear
                            picker="time"
                            format="HH:mm"
                            disabled={
                              schedulingPreference === 'realtime' ||
                              (!targetedNotificationEnabled && !summaryNotificationEnabled)
                            }
                          />
                        </Form.Item>
                        <Form.Item<NotificationOptions> name="frequency" label="Frequency (repeat every N hours)">
                          <Radio.Group
                            buttonStyle="solid"
                            disabled={
                              schedulingPreference === 'realtime' ||
                              (!targetedNotificationEnabled && !summaryNotificationEnabled)
                            }
                          >
                            <Radio.Button value={6}>6 hours</Radio.Button>
                            <Radio.Button value={8}>8 hours</Radio.Button>
                            <Radio.Button value={12}>12 hours</Radio.Button>
                            <Radio.Button value={24}>24 hours</Radio.Button>
                          </Radio.Group>
                        </Form.Item>
                      </div>
                      <Divider />
                      <Form.Item<NotificationOptions> name="summaryNotificationEnabled" valuePropName="checked">
                        <Checkbox onChange={handleToggleSummaryNotifications}>
                          <h4>Send HOS and teleport violations of all drivers to a Telegram group</h4>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item<NotificationOptions>
                        label="Group ID"
                        tooltip="Add the bot to the Telegram group first; its first message usually includes the group ID."
                        name="summaryGroupId"
                        rules={[
                          {
                            required: summaryNotificationEnabled,
                            message: 'The group id is required when violation summary notifications are enabled.',
                          },
                          {
                            pattern: /^-[1-9][0-9]*$/,
                            message: 'ID of the group must be a negative number',
                          },
                        ]}
                      >
                        <Input disabled={!summaryNotificationEnabled} allowClear />
                      </Form.Item>
                      <Divider />
                      <Form.Item<NotificationOptions> name="targetedNotificationEnabled" valuePropName="checked">
                        <Checkbox onChange={handleToggleTargetedNotifications}>
                          <div>
                            <h4>Notify each driver in their Telegram group that they have violations</h4>
                            <small style={{ display: 'block' }}>
                              Note: even with a realtime schedule, drivers are notified at most once every 24 hours.
                            </small>
                          </div>
                        </Checkbox>
                      </Form.Item>
                      <Form.Item<NotificationOptions>
                        name="shortcut"
                        label="Message template shortcut (optional)"
                        tooltip="Enter the template shortcut from Intelligent Support; if that template has translations, drivers receive the translated message. Leave blank to use the default message below."
                        rules={[
                          {
                            max: 128,
                            message: 'The shortcut can not exceed 128 characters.',
                          },
                          {
                            pattern: /^[\w-]+$/,
                            message: 'The shortcut can only contain Latin letters, numbers, dashes and underscores.',
                          },
                        ]}
                      >
                        <Input disabled={!targetedNotificationEnabled} allowClear />
                      </Form.Item>
                      <Form.Item<NotificationOptions>
                        name="message"
                        label="Default message"
                        tooltip="Used when no template shortcut is provided or when the shortcut isn’t found in your Intelligent Support account."
                        required
                        rules={[
                          {
                            required: targetedNotificationEnabled,
                            transform: (value?: string) => (value ? value.trim() : value),
                            message:
                              'A default message is required. If you want to cancel driver-specific notifications, please uncheck the checkbox above.',
                          },
                        ]}
                      >
                        <Input.TextArea disabled={!targetedNotificationEnabled} />
                      </Form.Item>
                      <Form.Item<NotificationOptions> name="preference" label="Notify about">
                        <Radio.Group disabled={!targetedNotificationEnabled}>
                          {Object.entries(NotificationPreferenceLabels).map(
                            ([
                              option,
                              label,
                            ]) => (
                              <Radio key={option} value={option}>
                                {label}
                              </Radio>
                            )
                          )}
                        </Radio.Group>
                      </Form.Item>
                    </Form>
                  </Spin>
                </Modal>
              </Col>
            )}
          </Row>
          <div className="content">
            <div style={{ padding: 16 }}>
              <Row
                gutter={[
                  8,
                  8,
                ]}
              >
                <Col>
                  <Tooltip getPopupContainer={getContainer} getTooltipContainer={getContainer} title="Select company">
                    <Select<string, { label: string; value: string }>
                      placeholder="Select company"
                      allowClear={true}
                      showSearch
                      getPopupContainer={getContainer}
                      loading={companiesLoading}
                      disabled={!companiesSubscribed}
                      options={Object.values(companiesById).map((company) => ({
                        label: company.name,
                        value: company._id,
                      }))}
                      value={companyId || undefined}
                      onChange={(companyId) => {
                        setCompanyId(companyId);
                        setDriverId(null);
                      }}
                      filterOption={fuzzySelectFilter}
                      style={{ minWidth: 150 }}
                    />
                  </Tooltip>
                </Col>
                <Col>
                  <Tooltip getPopupContainer={getContainer} getTooltipContainer={getContainer} title="Select driver">
                    <Select<string, { label: string; value: string }>
                      placeholder="Select driver"
                      allowClear={true}
                      showSearch
                      getPopupContainer={getContainer}
                      loading={driversLoading}
                      disabled={!driversSubscribed}
                      options={Object.values(driversById || {}).map((driver) => ({
                        label: `${driver.firstName} ${driver.lastName}`,
                        value: driver._id,
                      }))}
                      value={driverId || undefined}
                      onChange={(driverId) => setDriverId(driverId)}
                      filterOption={fuzzySelectFilter}
                      style={{ minWidth: 150 }}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </div>
            <Table<Violation>
              rowKey={(record) => `${record.companyId}-${record.driverId}`}
              dataSource={filteredViolations}
              loading={filteredViolations === undefined || patchIsBeingCreated}
              onChange={handleTableChange}
            >
              <Table.Column<Violation> title="Company Name" key="companyId" dataIndex="companyName" />
              <Table.Column<Violation> title="Driver Name" key="driverId" dataIndex="driverName" />
              <Table.Column<Violation> title="Total Violations" key="total_violations" dataIndex="totalViolations" />
              <Table.Column<Violation>
                title={
                  <span>
                    HOS Violations & Teleports{' '}
                    {filterSelections['regulations']?.length ? `(${filterSelections['regulations'].length})` : ''}
                  </span>
                }
                key="regulations"
                dataIndex="regulations"
                render={(violations: string[]) => violations?.join(', ') || ''}
                // use custom dropdown to support search + "Select all (matching search)"
                filterDropdown={(props) => <FilterDropdown options={regulationsFilters} {...props} />}
                onFilter={(value, record) => (record.regulations || []).includes(value as string)}
              />
              <Table.Column<Violation>
                title={
                  <span>
                    Non-HOS Violations{' '}
                    {filterSelections['non-hos-violations']?.length
                      ? `(${filterSelections['non-hos-violations'].length})`
                      : ''}
                  </span>
                }
                key="non-hos-violations"
                dataIndex="violations"
                render={(violations: string[]) => violations?.join(', ') || ''}
                filterDropdown={(props) => <FilterDropdown options={violationsFilters} {...props} />}
                onFilter={(value, record) => (record.violations || []).includes(value as string)}
              />
              <Table.Column<Violation>
                title={
                  <Tooltip title="Timestamp when this driver's events were last checked for potential violations.">
                    Last Checked
                  </Tooltip>
                }
                key="updated_at"
                dataIndex="updatedAt"
                render={(date: string) => (date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : null)}
                sorter={(a, b, sortOrder) =>
                  (b.updatedAt ? Date.parse(b.updatedAt) : sortOrder === 'ascend' ? -3e12 : 3e12) -
                  (a.updatedAt ? Date.parse(a.updatedAt) : sortOrder === 'ascend' ? -3e12 : 3e12)
                }
              />
              <Table.Column<Violation>
                title={
                  <Tooltip title="The date and time when the notification about violations was last sent.">
                    Last Notified
                  </Tooltip>
                }
                key="notified_at"
                dataIndex="lastNotifiedAt"
                render={(date: string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : null)}
                sorter={(a, b, sortOrder) =>
                  (b.lastNotifiedAt ? Date.parse(b.lastNotifiedAt) : sortOrder === 'ascend' ? -3e12 : 3e12) -
                  (a.lastNotifiedAt ? Date.parse(a.lastNotifiedAt) : sortOrder === 'ascend' ? -3e12 : 3e12)
                }
              />
              <Table.Column<Violation>
                title="Actions"
                key="actions"
                render={(_, record) => (
                  <Tooltip title="Create new log and load driver events">
                    <Button
                      type="primary"
                      icon={<FileAddOutlined />}
                      onClick={() => createManualPatch(record.companyId, record.driverId)}
                    >
                      Create
                    </Button>
                  </Tooltip>
                )}
              />
            </Table>
          </div>
        </Layout.Content>
      </Layout>
    </Spin>
  );
};
